import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import '../styles/login.css';
import logo from '../assets/logo_clt.gif';

export default function Login() {
    const [isLogin, setIsLogin] = useState(true);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const { login, signup, resetPassword } = useAuth();
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors, isSubmitting }, setError, clearErrors } = useForm();

    const onSubmit = async (data) => {
        try {
            if (isForgotPassword) {
                await resetPassword(data.email);
                setError('root', {
                    type: 'success',
                    message: "E-mail de réinitialisation envoyé ! Vérifiez votre boîte de réception."
                });
                return;
            }

            if (isLogin) {
                await login(data.email, data.password);
            } else {
                await signup(data.email, data.password);
            }
            navigate('/dashboard');
        } catch (error) {
            console.error("Auth error details:", error);

            let message = 'Failed to authenticate. Check console for details.';
            const errString = error.message || error.code || '';

            if (errString.includes('invalid-credential') || errString.includes('wrong-password') || errString.includes('user-not-found')) {
                message = 'Email ou mot de passe incorrect.';
            } else if (errString.includes('email-already-in-use')) {
                message = 'Cet email est déjà utilisé. Essayez de vous connecter.';
            } else if (errString.includes('weak-password')) {
                message = 'Le mot de passe doit contenir au moins 6 caractères.';
            } else if (errString.includes('operation-not-allowed')) {
                message = 'Connexion désactivée.';
            } else if (errString.includes('network-request-failed')) {
                message = 'Erreur réseau. Vérifiez votre connexion.';
            } else if (errString.includes('auth/invalid-email')) {
                message = "L'adresse email est invalide.";
            }

            setError('root', {
                type: 'manual',
                message: message
            });
        }
    };

    const toggleMode = () => {
        setError('root', null);
        clearErrors();
        if (isForgotPassword) {
            setIsForgotPassword(false);
            setIsLogin(true);
        } else {
            setIsLogin(!isLogin);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <img src={logo} alt="Inventaire U" className="login-logo" />
                    <h1 className="login-title">
                        {isForgotPassword ? 'Mot de passe oublié ?' : (isLogin ? 'Bienvenue' : 'Créer un compte')}
                    </h1>
                    <p className="login-subtitle">
                        {isForgotPassword
                            ? 'Entrez votre email pour recevoir un lien de réinitialisation.'
                            : (isLogin
                                ? 'Entrez vos identifiants pour gérer votre inventaire.'
                                : 'Créez un nouveau compte pour configurer votre bar.')}
                    </p>
                </div>

                {errors.root && (
                    <div className={`error-banner ${errors.root.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : ''}`}>
                        <AlertCircle size={18} className="shrink-0" />
                        <span>{errors.root.message}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="login-form">
                    <div className="form-group">
                        <label className="form-label">Adresse mail</label>
                        <input
                            {...register('email', { required: 'Mail requis' })}
                            type="email"
                            className="input"
                            placeholder="email@example.com"
                        />
                        {errors.email && <p className="form-error">{errors.email.message}</p>}
                    </div>

                    {!isForgotPassword && (
                        <div className="form-group">
                            <label className="form-label">Mot de passe</label>
                            <input
                                {...register('password', { required: 'Mot de passe requis' })}
                                type="password"
                                className="input"
                                placeholder="Entrez votre mot de passe"
                            />
                            {errors.password && <p className="form-error">{errors.password.message}</p>}
                        </div>
                    )}

                    {isLogin && !isForgotPassword && (
                        <div className="text-right">
                            <button
                                type="button"
                                onClick={() => { setIsForgotPassword(true); setError('root', null); }}
                                className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                            >
                                Mot de passe oublié ?
                            </button>
                        </div>
                    )}

                    <button type="submit" className="submit-btn" disabled={isSubmitting}>
                        {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : (isForgotPassword ? 'Envoyer le lien' : (isLogin ? 'Se connecter' : 'Créer un compte'))}
                    </button>
                </form>

                <button
                    onClick={toggleMode}
                    className="toggle-btn"
                >
                    {isForgotPassword
                        ? "Retour à la connexion"
                        : (isLogin ? "Pas de compte ? Créez-en un" : "Vous avez déjà un compte ? Connectez-vous")}
                </button>
            </div>
        </div>
    );
}
